import { Fragment, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Checks } from "@phosphor-icons/react";
import { Button, NotificationGroupLabel, NotificationItem } from "@hulahoop/ui";
import { groupNotificationsByTime } from "@/utils/notificationGrouping";
import { ShellInboxItem } from "./ShellInboxItem";
import type { ShellInboxState } from "./useShellInbox";

function useInfiniteScroll(inbox: ShellInboxState) {
  const sentinel = useRef<HTMLDivElement>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = inbox.query;
  useEffect(() => {
    const target = sentinel.current;
    if (!target || !hasNextPage) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting) && !isFetchingNextPage) {
        void fetchNextPage();
      }
    });
    observer.observe(target);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);
  return sentinel;
}

export function ShellInboxList({ inbox }: { inbox: ShellInboxState }) {
  const { t } = useTranslation();
  const sentinel = useInfiniteScroll(inbox);
  if (inbox.query.isPending) {
    return (
      <p role="status" className="px-3 py-10 text-center text-(length:--font-caption)">
        {t("notifications.loading")}
      </p>
    );
  }
  if (inbox.query.isError) {
    return (
      <div role="alert" className="flex flex-col items-center gap-1.5 px-3 py-10 text-center">
        <p className="text-(length:--font-caption) text-muted-foreground">
          {t("shell.inbox_failed")}
        </p>
        <Button variant="outline" size="sm" onClick={() => void inbox.query.refetch()}>
          {t("shell.retry")}
        </Button>
      </div>
    );
  }
  if (inbox.notifications.length === 0) {
    return (
      <div className="flex flex-col items-center gap-1.5 px-3 py-10 text-center">
        <Checks className="text-muted-foreground" aria-hidden />
        <p className="text-(length:--font-caption) text-muted-foreground">
          {t("notifications.all_caught_up")}
        </p>
      </div>
    );
  }
  return (
    <div className="min-h-0 flex-1 overflow-y-auto pb-1">
      {groupNotificationsByTime(inbox.notifications, t).map((group) => (
        <Fragment key={group.key}>
          <NotificationGroupLabel>{group.label}</NotificationGroupLabel>
          {group.notifications.map((notification) => (
            <NotificationItem key={notification.uuid}>
              <ShellInboxItem notification={notification} inbox={inbox} />
            </NotificationItem>
          ))}
        </Fragment>
      ))}
      <div ref={sentinel} aria-hidden />
    </div>
  );
}
